import React, { ReactElement } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Button } from '@infoods/lifebase-ui';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import classes from './FoodRow.module.scss';
import { FoodRowActions } from './FoodRowActions/FoodRowActions';
import {
  GridTableCell,
  GridTableRow,
} from '../../../../shared/components/table-components';
import { Nutrient, Status } from '../../../../shared/components/ui-components';
import { NUTRIENTS_ENUM } from '../../../../shared/constants/NUTRIENTS_ENUM';
import { loggedInUserDataStore } from '../../../../shared/stores/loggedInUserDataStore';
import { getValueOrDash } from '../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { FoodType } from '../../../constants/foodType';
import { FoodListItem } from '../../../interfaces/FoodListItem';
import { FOODS_TABLE_COLUMN, FoodsTableColumn } from '../NgzFoods.settings';
import {
  getRoundedNumber,
  getRoundedNumberOrDash,
} from '../../../../shared/utils/mathUtils';
import { goTo } from '../../../../shared/utils/angularUtils';
import { goToAddFoodWithTemplate } from '../../../utils';
import { FoodTagChip } from '../../../components/FoodTagChip/FoodTagChip';

type FoodRowProps = {
  food: FoodListItem;
  /** builds the edit link and selects the list store; rendered columns come from `visibleColumns` */
  foodType: FoodType;
  visibleColumns: FoodsTableColumn[];
};

const FoodRow = observer(({ food, foodType, visibleColumns }: FoodRowProps) => {
  const { isSuperAdmin } = loggedInUserDataStore;
  const editable = food.addedByUser || isSuperAdmin;

  // `add-food` prefills the clone from storage; see `saveSelectedFood` in foods.controller.js
  const handleEditClick = () => {
    if (editable) {
      goTo(`/edit-food/${foodType}/${food.id}`);
    } else {
      goToAddFoodWithTemplate(food, foodType);
    }
  };

  const renderColumnsInfo: Record<FoodsTableColumn, ReactElement> = {
    [FOODS_TABLE_COLUMN.NAME]: (
      <GridTableCell>
        <Button variant="link" onClick={handleEditClick} className={classes.nameCell}>
          {getValueOrDash(food.name_only)}
        </Button>
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.BRAND]: (
      <GridTableCell>{getValueOrDash(food.brand)}</GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.QTY]: (
      <GridTableCell>{getRoundedNumberOrDash(food.amount)}</GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.MEASUREMENT]: (
      <GridTableCell>{getValueOrDash(food.amountUnit)}</GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.PROTEIN]: (
      <GridTableCell>
        <Nutrient nutrient={NUTRIENTS_ENUM.PROTEINS} iconClassName={classes.nutrientIcon}>
          {getRoundedNumber(food.proteins, 1)}
        </Nutrient>
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.CARB]: (
      <GridTableCell>
        <Nutrient nutrient={NUTRIENTS_ENUM.CARBS} iconClassName={classes.nutrientIcon}>
          {getRoundedNumber(food.carbs, 1)}
        </Nutrient>
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.FATS]: (
      <GridTableCell>
        <Nutrient nutrient={NUTRIENTS_ENUM.FATS} iconClassName={classes.nutrientIcon}>
          {getRoundedNumber(food.fats, 1)}
        </Nutrient>
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.RECOMMENDATIONS]: (
      <GridTableCell>
        <Status
          icon={
            food.recommended ? (
              <CheckCircleRoundedIcon color="success" className={classes.statusIcon} />
            ) : (
              <CancelRoundedIcon color="tertiary" className={classes.statusIcon} />
            )
          }
        >
          {food.recommended ? 'Rec' : 'Not Rec'}
        </Status>
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.TAG]: (
      <GridTableCell className={classes.tagCell}>
        {food.tags.map((tag) => (
          <FoodTagChip key={tag.id} label={tag.name} size="small" />
        ))}
      </GridTableCell>
    ),
    [FOODS_TABLE_COLUMN.ACTIONS]: (
      <FoodRowActions
        food={food}
        foodType={foodType}
        editable={editable}
        onEditClick={handleEditClick}
      />
    ),
  };

  return (
    <GridTableRow
      variant="row"
      className={classNames({
        [classes.banned]: food.banned,
      })}
    >
      {visibleColumns.map((column) => (
        <React.Fragment key={column}>{renderColumnsInfo[column]}</React.Fragment>
      ))}
    </GridTableRow>
  );
});

export { FoodRow };
